import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleCheck,
  faLock,
  faPlus,
} from '@fortawesome/free-solid-svg-icons'
import GrowthWheelWorks from '../../components/GrowthWheelWorks/GrowthWheelWorks'
import GrowthWheelGraph from '../../components/GrowthWheelVisualizer/growth-wheel-graph'
import CompetencyCard from '../../components/GrowthWheelComponents/CompetencyCard'
import {
  setAssessmentCompletion,
  setCurrentCompetency,
  setCurrentSubCompetency,
} from '../../redux/growthWheel.slice'
import { RootState } from '../../redux/store'
import { Competency } from '../../types/GrowthWheel'
import {
  getCompletedCompetencies,
  getTotalCompetencies,
} from '../../utils/growth-wheel-utils'
import AreasOfFocus from '../../components/GrowthWheelComponents/AreasOfFocus'
import { useCompleteGrowthWheelAssessmentMutation } from '../../redux/growth-wheel.endpoints'
import { MarkCompletedModal } from '../../components/GrowthWheelComponents/MarkCompletedModal'

interface Props {
  isAdminView?: boolean
  userId?: number | null
  userName?: string
  guildName?: string
}

const GrowthWheelDashboard = ({
  isAdminView = false,
  userId,
  userName,
  guildName,
}: Props) => {
  const dispatch = useDispatch()
  const [showMarkCompletedModal, setShowMarkCompletedModal] = useState(false)
  const [completionError, setCompletionError] = useState('')
  const [completeGrowthWheelAssessment, { isLoading: isCompleting }] =
    useCompleteGrowthWheelAssessmentMutation()
  const growthWheelData = useSelector(
    (state: RootState) => state.growthWheel.growthWheelData,
  )
  const isLoading = useSelector(
    (state: RootState) => state.growthWheel.isLoadingGrowthWheel,
  )
  const userCompetenciesData = useSelector(
    (state: RootState) => state.growthWheel.userCompetenciesData,
  )
  const growthWheelCompleted = useSelector(
    (state: RootState) => state.growthWheel.growthWheelCompleted,
  )
  const assessmentCompleted = useSelector(
    (state: RootState) => state.growthWheel.assessmentCompleted,
  )
  const assessmentCompletedAt = useSelector(
    (state: RootState) => state.growthWheel.assessmentCompletedAt,
  )
  const completedDate = assessmentCompletedAt
    ? new Date(assessmentCompletedAt).toLocaleDateString()
    : null
  const allSubCompetencies = userCompetenciesData.flatMap(
    competency => competency.subCompetencies ?? [],
  )
  const incompleteCount = allSubCompetencies.filter(
    sub =>
      sub.evaluation?.selfLevel == null || sub.evaluation?.leaderLevel == null,
  ).length
  const canMarkCompleted =
    isAdminView &&
    !assessmentCompleted &&
    userId != null &&
    growthWheelData?.id != null &&
    allSubCompetencies.length > 0 &&
    incompleteCount === 0
  const assessmentName = growthWheelData?.name || 'Growth Wheel'

  const handleClickCompetency = (competency: Competency | null) => {
    dispatch(setCurrentCompetency(competency))
  }

  const ContinueAssessment = () => {
    const firstUncompletedSub = userCompetenciesData
      ?.flatMap(c => c.subCompetencies ?? [])
      .find(sub => !sub.evaluation?.selfLevel)
    if (firstUncompletedSub) {
      const parentCompetency = userCompetenciesData?.find(c =>
        c.subCompetencies?.some(s => s.id === firstUncompletedSub.id),
      )
      if (parentCompetency) {
        dispatch(setCurrentCompetency(parentCompetency))
        dispatch(setCurrentSubCompetency(firstUncompletedSub))
      }
    }
  }

  const shouldDisplayStartAssessment = userCompetenciesData
    ?.flatMap(c => c.subCompetencies ?? [])
    .every(sub => !sub.evaluation?.selfLevel)

  const handleConfirmCompleteAssessment = async () => {
    if (!canMarkCompleted || userId == null || growthWheelData?.id == null) {
      return
    }

    try {
      const result = await completeGrowthWheelAssessment({
        growthWheelId: growthWheelData.id,
        userId,
      }).unwrap()
      dispatch(
        setAssessmentCompletion({
          isCompleted: result.isCompleted,
          completedAt: result.completedAt,
        }),
      )
      setShowMarkCompletedModal(false)
      setCompletionError('')
    } catch {
      setCompletionError('Could not complete the assessment. Please try again.')
    }
  }

  return (
    <div className="container mb-5 gw-growth-wheel-container">
      <div className="my-5">
        <div className="gw-growth-wheel-dashboard-header">
          <div>
            <h1 className="gw-growth-wheel-title">
              {userName ? `${userName}'s` : ''} Growth Wheel
            </h1>
            {guildName ? (
              <p className="gw-growth-wheel-guild-name">{guildName}</p>
            ) : null}
          </div>
          {isAdminView && (
            <div className="gw-dashboard-completion-action">
              {assessmentCompleted ? (
                <button
                  type="button"
                  onClick={() =>
                    setCompletionError(
                      'Starting a new assessment is not available yet.',
                    )
                  }
                  className="gw-btn-submit"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Start New Assessment
                </button>
              ) : (
                <>
                  <div className="gw-completion-tooltip-wrapper">
                    <button
                      type="button"
                      onClick={() => {
                        setCompletionError('')
                        setShowMarkCompletedModal(true)
                      }}
                      disabled={!canMarkCompleted}
                      className="gw-btn-submit gw-btn-submit--success"
                    >
                      <FontAwesomeIcon icon={faCircleCheck} />
                      Mark as Completed
                    </button>
                    {!canMarkCompleted && (
                      <div className="gw-completion-tooltip">
                        {incompleteCount}{' '}
                        {incompleteCount === 1
                          ? 'competency is'
                          : 'competencies are'}{' '}
                        missing scores
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <p className="gw-growth-wheel-dashboard-tagline">
          Reflect on your competencies and prepare for meaningful growth
          conversations.
        </p>
        {completionError && (
          <div className="alert alert-danger small mt-3">{completionError}</div>
        )}
        {assessmentCompleted && (
          <div className="gw-assessment-locked-banner mt-3">
            <FontAwesomeIcon icon={faLock} />
            <span>
              This assessment is completed
              {completedDate ? ` as of ${completedDate}` : ''}. All fields are
              locked.
            </span>
          </div>
        )}
      </div>
      <div className="row">
        <div className="col-10 col-md-4 gw-visualizer-container">
          {isLoading ? (
            <div>Loading...</div>
          ) : (
            <>
              <GrowthWheelGraph
                areas={userCompetenciesData.map(area => ({
                  id: area.id,
                  name: area.name,
                  subCompetencies: (area.subCompetencies ?? []).map(
                    competency => ({
                      id: competency.id,
                      name: competency.name,
                    }),
                  ),
                }))}
              />
              <GrowthWheelWorks />
            </>
          )}
        </div>
        <div className="col-12 col-md-8 gw-competency-area-cards-container">
          <div className="gw-competency-area-cards-header">
            <h3 className="gw-competency-area-cards-header-title">
              Competencies
            </h3>
            {!isAdminView && !growthWheelCompleted && !assessmentCompleted && (
              <Button onClick={ContinueAssessment}>
                {shouldDisplayStartAssessment
                  ? 'Start Assessment'
                  : 'Continue Assessment'}
              </Button>
            )}
          </div>
          <AreasOfFocus />
          <hr />
          <div className="gw-competency-area-list">
            {userCompetenciesData.map(competency => (
              <CompetencyCard
                key={competency.id}
                title={competency.name}
                description={competency.description}
                onClick={() => handleClickCompetency(competency)}
                completed={getCompletedCompetencies(
                  competency.subCompetencies ?? [],
                )}
                total={getTotalCompetencies(competency.subCompetencies ?? [])}
              />
            ))}
          </div>
        </div>
      </div>
      <MarkCompletedModal
        show={showMarkCompletedModal}
        contributorName={userName || 'this contributor'}
        assessmentName={assessmentName}
        isSubmitting={isCompleting}
        onConfirm={handleConfirmCompleteAssessment}
        onCancel={() => setShowMarkCompletedModal(false)}
      />
    </div>
  )
}

export default GrowthWheelDashboard
